'use client';

import { useTranslations } from 'next-intl';
import {
  AlertTriangle,
  CheckCircle2,
  FileText,
  ImageIcon,
  Palette,
  XCircle,
} from 'lucide-react';
import { LoadingSpinner } from '@/shared/ui';
import { Button } from '@/shared/ui/shadcn/button';
import { DIFFERENT_BUILDING_REASON } from '@/types/project-angle';
import type { ValidationItem } from './types';

interface ValidationItemsListProps {
  items: ValidationItem[];
  /**
   * Promote an angle to a valid view via POST /angles/{id}/confirm-building.
   * Drives two CTAs:
   *  - STRICT block (`different_building_block`): "It's my building".
   *  - LENIENT "added as a reference" (`added_as_reference`): "Use as a view"
   *    (server restores the angle as a view + deletes the routed reference).
   * Both are gated on the server's `can_override_building` flag.
   */
  onConfirmBuilding?: (id: string) => void;
  /** Id of the row whose override is in flight (disables its button). */
  confirmingId?: string | null;
  /**
   * Convert an AUTO-ACCEPTED view back to a reference ("Make it a reference").
   * Offered on a valid row with `auto_accepted_as_view=true` — the classifier
   * read the image as inspiration but the server kept it as a view by default.
   * Runs the shared view → reference conversion chain (materials convert +
   * section re-tag) in the parent.
   */
  onMakeReference?: (id: string) => void;
  /** Id of the row whose view → reference conversion is in flight. */
  makingReferenceId?: string | null;
}

/**
 * A VALID row the server auto-accepted as a view: the classifier filed the
 * first upload of a building as inspiration, but the server kept it as a view
 * by default. Accepted (info tint), with the server note and a
 * "Make it a reference" undo action.
 */
function isAutoAcceptedView(item: ValidationItem): boolean {
  return (
    item.status === 'valid' && item.validation?.auto_accepted_as_view === true
  );
}

/** A STRICT different-building block the server says can be overridden. */
function canOverrideBuilding(item: ValidationItem): boolean {
  return (
    (item.status === 'different_building_block' ||
      // Legacy: a plain invalid row still carrying the different-building reason.
      (item.status === 'invalid' &&
        item.validation?.validation_failure_reason ===
          DIFFERENT_BUILDING_REASON)) &&
    item.validation?.can_override_building === true
  );
}

/**
 * Any AUTO-ROUTED row the user can promote to a view in one tap. The classifier
 * accepted the upload but filed it elsewhere — as a reference (added_as_reference
 * / moved_to_references), a Brief document (moved_to_documents) or an output style
 * (moved_to_output_style). The server gates the promotion with can_override_building
 * (true for every promotable routed outcome) and reverses the route on confirm.
 */
function canUseAsView(item: ValidationItem): boolean {
  return (
    (item.status === 'added_as_reference' ||
      item.status === 'moved_to_references' ||
      item.status === 'moved_to_documents' ||
      item.status === 'moved_to_output_style') &&
    item.validation?.can_override_building === true
  );
}

/**
 * Per-image status rows: thumbnail — status label + failure reason — status
 * icon. Mirrors the Apple ImageStatusRow layout: the row title is the status
 * text (not the filename), the failure reason renders as secondary meta.
 */
export function ValidationItemsList({
  items,
  onConfirmBuilding,
  confirmingId,
  onMakeReference,
  makingReferenceId,
}: ValidationItemsListProps) {
  const t = useTranslations('angle.upload.modal');
  const tCommon = useTranslations('common');
  const tOptions = useTranslations('render.options');

  function statusTitle(item: ValidationItem): string {
    // Auto-accepted view — same valid status, but titled for what happened.
    if (isAutoAcceptedView(item)) {
      return t('statusAddedAsView');
    }
    switch (item.status) {
      case 'uploading':
        return t('phaseUploading');
      case 'validating':
        return t('statusValidating');
      case 'valid':
        return t('statusValid');
      case 'invalid':
        return t('statusInvalid');
      case 'moved_to_documents':
        return t('statusMovedToDocuments');
      case 'moved_to_references':
        return t('statusMovedToReferences');
      case 'moved_to_output_style':
        return t('statusMovedToOutputStyle');
      case 'added_as_reference':
        return t('statusAddedAsReference');
      case 'different_building_block':
        return t('statusDifferentBuilding');
      case 'error':
        return tCommon('error');
    }
  }

  function statusIcon(item: ValidationItem) {
    // Auto-accepted view: accepted, but with the routed rows' info tint (teal)
    // so it reads as a classification call the user may want to revisit.
    if (isAutoAcceptedView(item)) {
      return (
        <CheckCircle2 className="h-6 w-6 text-[#078D86] dark:text-teal-400" />
      );
    }
    switch (item.status) {
      case 'uploading':
      case 'validating':
        return <LoadingSpinner className="h-6 w-6" />;
      case 'valid':
        return (
          <CheckCircle2 className="h-6 w-6 text-green-600 dark:text-green-400" />
        );
      case 'moved_to_documents':
        // Neutral/positive — teal, matching the NoticeBanner info palette.
        return <FileText className="h-6 w-6 text-[#078D86] dark:text-teal-400" />;
      case 'moved_to_references':
        // Neutral/positive — teal, an image moved to the inspiration shelf.
        return <ImageIcon className="h-6 w-6 text-[#078D86] dark:text-teal-400" />;
      case 'moved_to_output_style':
        // Neutral/positive — teal, an illustrated rendering moved to the
        // References as an output-style reference.
        return <Palette className="h-6 w-6 text-[#078D86] dark:text-teal-400" />;
      case 'added_as_reference':
        // Lenient different-building → accepted as a reference. Neutral/positive
        // teal, same as the other "moved to references" outcomes.
        return <ImageIcon className="h-6 w-6 text-[#078D86] dark:text-teal-400" />;
      case 'different_building_block':
        // Strict hard block — needs the user to confirm or cancel. Amber.
        return <AlertTriangle className="h-6 w-6 text-orange-500" />;
      case 'invalid':
        return <AlertTriangle className="h-6 w-6 text-orange-500" />;
      case 'error':
        return <XCircle className="h-6 w-6 text-destructive" />;
    }
  }

  return (
    <div className="space-y-3 flex-1 min-h-0 overflow-y-auto">
      {items.map((item) => (
        <div
          key={item.id}
          className="flex items-center gap-3 rounded-xl bg-muted/50 p-3"
        >
          {item.thumbnailUrl ? (
            // Local blob URL — next/image can't optimize object URLs.
            <img
              src={item.thumbnailUrl}
              alt=""
              className="h-[60px] w-[60px] flex-shrink-0 rounded-lg object-cover"
            />
          ) : (
            <div className="flex h-[60px] w-[60px] flex-shrink-0 items-center justify-center rounded-lg bg-muted">
              {/* A document row has no image — use the file glyph, not the
                  generic photo placeholder. */}
              {item.kind === 'document' ? (
                <FileText className="h-5 w-5 text-muted-foreground" />
              ) : (
                <ImageIcon className="h-5 w-5 text-muted-foreground" />
              )}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="text-list-item-value">{statusTitle(item)}</p>
            {/* Document row: show the filename so the user knows which file
                landed in their project documents. */}
            {item.kind === 'document' && item.fileName && (
              <p className="text-card-meta text-muted-foreground mt-1 truncate">
                {item.fileName}
              </p>
            )}
            {(item.status === 'invalid' || item.status === 'error') &&
              item.error && (
                <p className="text-card-meta text-muted-foreground mt-1">
                  {item.error}
                </p>
              )}
            {/* Server-localized message for every routed / classified / blocked
                outcome, plus the auto-accepted-view explanation (API-driven,
                shown verbatim). */}
            {(item.status === 'moved_to_documents' ||
              item.status === 'moved_to_references' ||
              item.status === 'moved_to_output_style' ||
              item.status === 'added_as_reference' ||
              item.status === 'different_building_block' ||
              isAutoAcceptedView(item)) &&
              item.note && (
                <p className="text-card-meta text-muted-foreground mt-1">
                  {item.note}
                </p>
              )}
            {/* STRICT block: confirm it really is this building → valid view.
                Kept a MODEST secondary (outline) button with no glyph — the
                classification is usually correct, so this correction must not read
                as the primary action to rush for. */}
            {onConfirmBuilding && canOverrideBuilding(item) && (
              <Button
                size="sm"
                variant="outline"
                className="mt-2"
                onClick={() => onConfirmBuilding(item.id)}
                disabled={confirmingId === item.id}
              >
                {tOptions('itsMyBuilding')}
              </Button>
            )}
            {/* Routed outcome: promote it to a view instead. Server restores the
                angle as a view and deletes the routed reference. Same modest
                secondary style. */}
            {onConfirmBuilding && canUseAsView(item) && (
              <Button
                size="sm"
                variant="outline"
                className="mt-2"
                onClick={() => onConfirmBuilding(item.id)}
                disabled={confirmingId === item.id}
              >
                {t('useAsView')}
              </Button>
            )}
            {/* Auto-accepted view: offer the reverse correction — file it as a
                reference instead. Same modest secondary style (the default
                call is usually right). */}
            {onMakeReference && isAutoAcceptedView(item) && (
              <Button
                size="sm"
                variant="outline"
                className="mt-2"
                onClick={() => onMakeReference(item.id)}
                disabled={makingReferenceId === item.id}
              >
                {t('makeItReference')}
              </Button>
            )}
          </div>
          <div className="flex-shrink-0">{statusIcon(item)}</div>
        </div>
      ))}
    </div>
  );
}
